// src/components/UpgradeNudge.jsx
// Proactive upsell notification — shown when free quota ≥ 50% used
// Dismissible per session. One nudge at a time (highest priority module).

import React, { useEffect, useState } from 'react';

const mono = 'JetBrains Mono, monospace';
const display = 'Syne, sans-serif';

const SESSION_KEY = 'dd_upgrade_nudge_dismissed';
const NUDGE_DELAY_MS = 4000; // wait 4s before showing

// Readable labels for module IDs
const MODULE_LABELS = {
  auto_eda:           { label: 'Auto EDA',           icon: '📊', color: 'var(--cyan)' },
  doc_analysis:       { label: 'Document Analysis',  icon: '📄', color: 'var(--violet)' },
  image_insights:     { label: 'Image Insights',      icon: '🖼️',  color: 'var(--pink)' },
  speech_studio:      { label: 'Speech Studio',       icon: '🎙️', color: 'var(--gold)' },
  ml_experiments:     { label: 'ML Experiments',      icon: '⚡', color: 'var(--orange)' },
  dl_experiments:     { label: 'DL Experiments',      icon: '🧠', color: 'var(--green)' },
  video_intelligence: { label: 'Video Intelligence',  icon: '🎞️', color: 'var(--orange)' },
};

// ── Mini progress bar ─────────────────────────────────────────────────────────
function MiniBar({ pct, color }) {
  return (
    <div style={{ height: 3, background: 'var(--b1)', borderRadius: 2, overflow: 'hidden', width: '100%' }}>
      <div style={{
        height: '100%', width: `${Math.min(100, pct)}%`, background: color,
        borderRadius: 2, transition: 'width .5s ease',
      }} />
    </div>
  );
}

// ── Derive highest-priority nudge from usageSummary ───────────────────────────
function getTopNudge(usageSummary) {
  if (!usageSummary?.modules) return null;

  let top = null;
  let topPct = 0;

  for (const [id, data] of Object.entries(usageSummary.modules)) {
    if (!MODULE_LABELS[id]) continue;
    const used = data?.used ?? 0;
    const limit = data?.limit ?? 0;
    if (limit <= 0) continue;
    const pct = used / limit;
    if (pct >= 0.5 && pct > topPct) {
      top = { id, used, limit, pct };
      topPct = pct;
    }
  }

  return top;
}

function isSessionDismissed() {
  try {
    return sessionStorage.getItem(SESSION_KEY) === '1';
  } catch {
    return false;
  }
}

function setSessionDismissed() {
  try {
    sessionStorage.setItem(SESSION_KEY, '1');
  } catch {}
}

// ── Main export ───────────────────────────────────────────────────────────────
export default function UpgradeNudge({ usageSummary, isPremium, onUpgrade }) {
  const [visible, setVisible] = useState(false);
  const [nudge, setNudge] = useState(null);

  useEffect(() => {
    if (isPremium || isSessionDismissed()) return;

    const top = getTopNudge(usageSummary);
    if (!top) return;

    const timer = setTimeout(() => {
      setNudge(top);
      setVisible(true);
    }, NUDGE_DELAY_MS);

    return () => clearTimeout(timer);
  }, [usageSummary, isPremium]);

  const handleDismiss = () => {
    setSessionDismissed();
    setVisible(false);
  };

  const handleUpgrade = () => {
    setSessionDismissed();
    setVisible(false);
    onUpgrade?.();
  };

  if (!visible || !nudge) return null;

  const meta = MODULE_LABELS[nudge.id] || { label: nudge.id, icon: '⚡', color: 'var(--cyan)' };
  const pct = Math.round(nudge.pct * 100);
  const isNearLimit = nudge.pct >= 0.8;
  const isAtLimit = nudge.used >= nudge.limit;

  return (
    <div
      style={{
        position: 'fixed',
        bottom: 24,
        left: 24,
        zIndex: 180,
        width: 'min(340px, calc(100vw - 48px))',
        background: 'var(--surface)',
        border: `1px solid ${isAtLimit ? 'var(--red)55' : isNearLimit ? 'var(--orange)55' : 'var(--b1)'}`,
        borderRadius: 10,
        boxShadow: '0 16px 48px rgba(0,0,0,.4)',
        overflow: 'hidden',
        animation: 'authSlideUp .3s cubic-bezier(.16,1,.3,1)',
      }}
    >
      {/* Accent bar */}
      <div style={{ height: 2, background: isAtLimit ? 'var(--red)' : isNearLimit ? 'var(--orange)' : meta.color }} />

      <div style={{ padding: '14px 16px' }}>
        {/* Header row */}
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 10, marginBottom: 12 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ fontSize: 18 }}>{meta.icon}</span>
            <div>
              <div style={{ fontSize: 9, color: isAtLimit ? 'var(--red)' : isNearLimit ? 'var(--orange)' : meta.color, letterSpacing: '.14em', fontFamily: mono, fontWeight: 800 }}>
                {isAtLimit ? 'FREE LIMIT REACHED' : isNearLimit ? 'NEARLY AT LIMIT' : 'USAGE NOTICE'}
              </div>
              <div style={{ fontFamily: display, fontWeight: 800, fontSize: 13, color: 'var(--t1)', marginTop: 2 }}>
                {meta.label}
              </div>
            </div>
          </div>
          <button
            onClick={handleDismiss}
            style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--t3)', fontSize: 13, padding: 2, flexShrink: 0 }}
            aria-label="Dismiss"
          >
            ✕
          </button>
        </div>

        {/* Usage bar */}
        <div style={{ marginBottom: 12 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
            <span style={{ fontSize: 10, color: 'var(--t2)', fontFamily: mono }}>{nudge.used} of {nudge.limit} free runs used</span>
            <span style={{ fontSize: 10, color: isAtLimit ? 'var(--red)' : isNearLimit ? 'var(--orange)' : meta.color, fontFamily: mono, fontWeight: 800 }}>
              {pct}%
            </span>
          </div>
          <MiniBar pct={pct} color={isAtLimit ? 'var(--red)' : isNearLimit ? 'var(--orange)' : meta.color} />
        </div>

        {/* Message */}
        <p style={{ fontSize: 11, color: 'var(--t2)', lineHeight: 1.7, marginBottom: 14 }}>
          {isAtLimit
            ? `You've used all free ${meta.label} runs this month. Upgrade to keep working without interruption.`
            : `You're at ${pct}% of the free ${meta.label} quota. Upgrade now to never hit a limit.`}
        </p>

        {/* CTA */}
        <div style={{ display: 'flex', gap: 8 }}>
          <button
            onClick={handleUpgrade}
            style={{
              flex: 1, padding: '9px', background: 'var(--cyan)', border: 'none', borderRadius: 5,
              color: '#020508', fontFamily: mono, fontSize: 9, letterSpacing: '.12em', fontWeight: 900, cursor: 'pointer',
            }}
          >
            UPGRADE TO RESEARCHER
          </button>
          <button
            onClick={handleDismiss}
            style={{
              padding: '9px 12px', background: 'transparent', border: '1px solid var(--b1)', borderRadius: 5,
              color: 'var(--t3)', fontFamily: mono, fontSize: 9, cursor: 'pointer',
            }}
          >
            NOT NOW
          </button>
        </div>
      </div>
    </div>
  );
}
